/**
 * UC7 — UC1's issuance UNCHANGED, with the rules layer on Aurora PostgreSQL Global Database.
 *
 * ── THIS PANEL'S ONE JOB: RENDER A THREE-STATE DISTINCTION, NOT A TWO-STATE ONE ─────────────
 *
 * UC7 used to be a DESIGN, and this panel used to be built entirely around saying so — no vend
 * control, a red not-built alert, a test-plan table reading "0 of 8 run". All of that was correct
 * then. IT IS NOW WRONG. There is an Aurora PostgreSQL Global Database deployed, `/v7` vends real
 * barcodes in both regions, and the topology has been proven by driving it.
 *
 * ⚠️ BUT UC7 MOVED FROM "PLANNED" TO "DEPLOYED" — *NOT* TO "MEASURED". The distinction this panel
 * now has to carry is three-state:
 *
 *     PLANNED     was true. No longer.
 *     DEPLOYED    it is. It vends. Its responses prove which engine and which role answered.
 *     MEASURED    IT IS NOT. No load test, no switchover, no failover drill, no patch test.
 *
 * "Deployed and vending" is not "measured". Collapsing them is how a working endpoint silently
 * becomes a performance claim nobody made — and performance measurement for UC7 is IN FLIGHT
 * ELSEWHERE as this panel is written, which is exactly why this panel must not anticipate it.
 *
 * ── THERE IS NOW A VEND CONTROL, AND IT MUST PROVE WHICH TOPOLOGY ANSWERED ───────────────────
 *
 * Modelled on Uc4Panel: live-only, no local fallback, and it ASSERTS `engine` on the way back.
 * The assertion matters MORE here than it does on UC4, and the reason is worth stating because it
 * is not obvious. A `/v4` response that had really come from Aurora PostgreSQL would eventually
 * betray itself — different dialect quirks, different error text. But `/v7` and `/v2` run the SAME
 * store class over the SAME engine version (PostgreSQL 16.9) against the SAME seeded rows, so a
 * `/v7` that had quietly answered from UC2's INDEPENDENT cluster would return a completely
 * plausible barcode. It would be INDISTINGUISHABLE BY INSPECTION. So the panel refuses any response
 * that does not report `engine: "aurora-global-postgresql"`, and it surfaces `globalRole` so a
 * viewer sees primary-versus-secondary for the region that actually answered.
 *
 * ── STILL NOT ONE PERFORMANCE FIGURE. THAT RULE DID NOT RELAX WHEN THE THING GOT BUILT ──────
 *
 * Every figure elsewhere on this site comes from a live API call or from a build-time parser over a
 * committed report. UC7 has no published performance numbers, so it gets none — not a projection,
 * not an arithmetic estimate, not a UC2 latency borrowed because the engine is nominally the same,
 * and NOT a number lifted out of the build log now that a build log exists. A hand-typed figure
 * from this site has already become a wrong number in a customer's own written analysis. The ONE
 * number this panel prints is the round-trip duration of the vend the viewer just triggered, which
 * is measured in their own browser at the moment they press the button — the same treatment every
 * other panel gives its own round trip, and clearly not a benchmark.
 *
 * ── AND IT MUST NOT BECOME AN ADVOCACY PAGE NOW THAT IT WORKS ───────────────────────────────
 *
 * A page listing only what UC7 gained by being built is a sales sheet. Two of the most consequential
 * findings of the build cut AGAINST UC7 — the RDS-managed-password incompatibility, and the master
 * credential with no IAM-shaped control over DDL — and both are on the page, in the costs table,
 * flagged as deciding the option. R7 is still NOT MET IN LETTER and that verdict is not softened
 * anywhere below.
 *
 * ── THE CAVEATS ARE IMPORTED, NOT RESTATED ─────────────────────────────────────────────────
 *
 * R7-not-met, DDL-not-forwarded, switchover-is-still-an-outage, the Data API verdict, the two
 * anti-UC7 findings, database-enforced integrity and "not measured" all live in src/lib/caveats.js,
 * for the reason that file's header gives: two copies of a caveat drift, and the one that drifts is
 * the one that gets softened. scripts/verify-web-live.sh asserts them in the rendered DOM.
 */

import { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Table from '@cloudscape-design/components/table';
import Badge from '@cloudscape-design/components/badge';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import SerialCard from './SerialCard.jsx';
import RawJson from './RawJson.jsx';
import { barcodeFromApiItem } from '../lib/impb.js';
import { timedFetch, errorText, isPlaceholder } from '../lib/api.js';
import {
  CUSTOMER_OPTIONS,
  SERVICE_OPTIONS,
  OBSERVED_RESOLUTIONS,
  DEFAULT_SERVICE,
  ZIP_DESCRIPTION,
  zipProblem,
  parseWeightOz,
  bandForOz,
  optionFor,
} from '../lib/rules-catalog.js';
import {
  UC7_MEASURED_AND_NOT_CHOSEN,
  UC7_OUTAGES_MEASURED_AND_NOT_OUR_DEFECT,
  UC7_INTRINSIC_VS_SURMOUNTABLE,
  UC7_CONNECTION_CLIFF_WAS_OUR_ARTEFACT,
  UC7_ROLE_AND_FORWARDING_DEFECTS_FIXED,
  MEASURED_COST_VS_UNMEASURED_UNKNOWN,
  UC7_R7_NOT_MET,
  UC7_DDL_NOT_FORWARDED,
  UC7_SWITCHOVER_IS_STILL_AN_OUTAGE,
  UC7_DATA_API_INCOMPATIBLE,
  UC7_REFERENTIAL_INTEGRITY_DB_ENFORCED,
  UC7_STATEMENT_TIMEOUT,
  UC7_MANAGED_PASSWORD_INCOMPATIBLE,
  UC7_CREDENTIAL_AXIS,
  UC4_FK3_RESIDUAL,
} from '../lib/caveats.js';
// Derived at build time by web/uc7-eval-summary.mjs from the COMMITTED per-sample probe JSON under
// loadtest/results/uc7-eval/. Every figure on this tab comes from here or from the live round trip —
// none is typed. See that file's header for why parsing its own input beats parsing the report.
import EVAL from '../generated/uc7-eval.json';

/**
 * The engine string every /v7 response must report, named once so the assertion, the prose and the
 * error message cannot drift apart. `aurora-postgresql` (UC2's Data API path) and `aurora-dsql`
 * (UC4) are the two values that would mean a different store answered under this heading.
 */
const UC7_ENGINE = 'aurora-global-postgresql';

/**
 * Render one region's caller-visible outage for a topology event.
 *
 * ⚠️ THE ONLY INTERESTING LINE HERE IS THE `stillFailing` BRANCH, AND IT EXISTS BECAUSE THE HONEST
 * NUMBER AND THE AVAILABLE NUMBER ARE NOT THE SAME NUMBER. When the probe stopped observing while
 * requests were still failing, `outageMs` is a FLOOR and not a duration — us-west-2's failover
 * observed 569,136 ms against a true ~1,189,149 ms established afterwards from Aurora's own events,
 * so printing it bare would understate the worst outage this system produced by 2.09x, in the
 * flattering direction, on the option being rejected.
 *
 * The bound is driven off the FLAG rather than a typed caveat so it cannot be forgotten, and so a
 * future re-run that observes recovery drops it automatically. `0` is rendered explicitly rather
 * than as an em dash: zero outage in one region during the patch is a RESULT, and a blank cell
 * reads as missing data.
 */
function formatOutage(region) {
  if (!region || typeof region.outageMs !== 'number') return <Box variant="small">not observed</Box>;
  if (region.outageMs === 0) {
    return <StatusIndicator type="success">0 ms — served throughout</StatusIndicator>;
  }
  return (
    <StatusIndicator type="error">
      {region.stillFailing ? '\u2265 ' : ''}
      {region.outageMs.toLocaleString()} ms
      {region.stillFailing ? (
        <>
          {' '}
          <Box variant="small" display="inline">
            (lower bound — still failing when observation ended)
          </Box>
        </>
      ) : null}
    </StatusIndicator>
  );
}

/**
 * What UC7 gains over UC4, and the counter-argument to each, because a gains list with no
 * counter-argument is a sales sheet. R14 is the customer's own statement that rules are ALREADY
 * enforced at application level today — which is the strongest thing anyone can say against the
 * headline gain, so it is printed next to the headline gain.
 */
const GAINS = [
  {
    what: 'Real constraint enforcement in the database — and the running system says so',
    detail: UC7_REFERENTIAL_INTEGRITY_DB_ENFORCED,
    against:
      'R14 is stated in the same string above rather than only here, deliberately — it is the strongest argument against the headline gain and it must not be reachable only by reading a second column. Beyond R14: database-enforced integrity is a property of the ENGINE, and it is equally true of UC2, which is already deployed and does not miss R7. So this gain argues against UC4; it does not on its own argue for UC7 over UC2.',
  },
  {
    what: 'Closes UC4’s sharpest residual risk',
    detail: UC4_FK3_RESIDUAL,
    against:
      'Aurora rejects the bad rule at write time instead of merely making it unselectable, and the seeded schema really does carry the foreign keys and CHECK constraints that do it. That is an improvement in kind, not degree — but again it is a property UC2 already has, so it separates UC7 from UC4 and not from the deployed PostgreSQL option.',
  },
  {
    what: 'A server-side statement timeout, which UC4 structurally cannot have',
    detail: UC7_STATEMENT_TIMEOUT,
    against:
      'It is a small advantage and it is presented as one. Nothing in the stated requirements asks for it, and it was found by driving the transport rather than by anyone requesting it. It should not be allowed to carry weight against R7.',
  },
  {
    what: 'A familiar operational model',
    detail:
      'Standard PostgreSQL, standard tooling, standard skills, patch-level control. The customer’s stated pain is operational — a duplicate incident, plus a Redis fault that was never root-caused — and an engine their team already knows how to debug is a real answer to that.',
    against:
      'Familiarity is not a measurement. It does not appear in any requirement, and it must not be allowed to outweigh R7, which UC7 does not meet. And the build found the unfamiliar edges are real: the RDS private CA is not in Node’s trust store, so the transport had to ship a vendored certificate bundle that UC4 needs no equivalent of.',
  },
  {
    what: 'No optimistic-concurrency retry logic',
    detail:
      'Aurora DSQL raises SQLSTATE 40001 on write conflict and the caller must retry. UC7 removes that code path from the application entirely.',
    against:
      'At this workload’s write rate the retry path may almost never fire. The write RATE is unconfirmed (U6) and it does real work in this argument — do not assume a number for it.',
  },
  {
    what: 'RPO 0 on a planned switchover',
    detail:
      'A switchover syncs all secondaries before promoting, so no committed write is lost. Cross-region replication lag is observable through the AuroraGlobalDBReplicationLag CloudWatch metric, and that metric was confirmed to be EMITTED for this topology — in the SECONDARY’s region only, which is worth knowing because a monitor that looks for it beside the primary finds nothing and would wrongly conclude it is unavailable.',
    against: UC7_SWITCHOVER_IS_STILL_AN_OUTAGE,
  },
];

/**
 * What UC7 COSTS. None of this may be softened.
 *
 * ⚠️ THE TWO NEW ROWS ARE THE ONES THAT MAKE THIS TABLE EVIDENCE RATHER THAN A DISCLAIMER.
 * `UC7_MANAGED_PASSWORD_INCOMPATIBLE` and `UC7_CREDENTIAL_AXIS` were both discovered by BUILDING
 * UC7 — neither appears in any planning document — and both cut AGAINST it. A page that gained a
 * working vend button and lost its objections would be advocacy. So the build's findings arrive in
 * BOTH directions or the page does not get to claim it is evidence.
 *
 * The first row is still the one that decides the option: R7 is a requirement MISSED, not a
 * trade-off absorbed, and being deployed does not change that by one word.
 */
const COSTS = [
  {
    what: 'Single writer — R7 is NOT met in letter',
    severity: 'red',
    detail: UC7_R7_NOT_MET,
  },
  {
    what: 'DDL is not forwarded — this IS the R8 patching case',
    severity: 'red',
    detail: UC7_DDL_NOT_FORWARDED,
  },
  {
    what: 'Incompatible with the RDS-managed master password — measured, and it cost a whole cluster',
    severity: 'red',
    detail: UC7_MANAGED_PASSWORD_INCOMPATIBLE,
  },
  {
    what: 'UC7 reads the MASTER credential, with no IAM control withholding DDL from the runtime',
    severity: 'red',
    detail: UC7_CREDENTIAL_AXIS,
  },
  {
    what: 'A write-rejection window is an outage under A-U1',
    severity: 'red',
    detail:
      'Under A-U1 (OUR ASSUMPTION, pending the customer’s answer on what "zero outages" means) a window in which reads succeed and writes are rejected IS an outage for a write request. A-U1 is the STRICTER of the two readings and was chosen deliberately: designing for it and later being told a write-rejection window is acceptable is a cheap correction, whereas the reverse misses a stated requirement. This is an internal assumption and NOT something the customer said.',
  },
  {
    what: 'The RDS Data API cannot reach a secondary — a code change, now written',
    severity: 'red',
    detail: UC7_DATA_API_INCOMPATIBLE,
  },
  {
    what: 'Statements that forwarding refuses outright',
    severity: 'grey',
    detail:
      'SERIALIZABLE isolation, user-defined functions and procedures, an explicit sequence nextval()/setval(), TRUNCATE, VACUUM, COPY, LOCK, SAVEPOINT, cursors, GRANT/REVOKE, SELECT INTO, ANALYZE, CLUSTER, SET CONSTRAINTS, LISTEN/NOTIFY and two-phase commit are all unsupported through write forwarding — TRUNCATE, GRANT, ANALYZE, VACUUM and an explicit nextval() were each DRIVEN against the live secondary and each refused with SQLSTATE 25006. One non-obvious result: an INSERT into a table whose key is GENERATED ALWAYS AS IDENTITY DOES forward and DOES get an identity value, even though a bare nextval() on that same sequence is refused. ⚠️ That is undocumented behaviour that happens to work, not a contract — so the design rule stands unchanged: never put a sequence on a forwarded write path, and route those mutations to the primary explicitly.',
  },
  {
    what: 'Serverless v2 as a global primary — the recommendation is ADVISORY, not enforced',
    severity: 'grey',
    detail:
      'This was a NAMED UNKNOWN and it is now answered by driving it: AWS documents a recommended minimum capacity for a Serverless v2 global primary, and the attach SUCCEEDED below that recommendation with no warning, no error and no coercion of the value. Both members reached a connected synchronisation status and replication works. So the recommendation is advisory at the API — but the consequence is stated rather than hidden: the deployed topology runs at a capacity ceiling BELOW the recommendation, which is a confound for any future load measurement and is a reason to raise the ceiling before measuring rather than a reason to trust a figure taken under it. No capacity figure is printed here on purpose; the numbers belong where they can be checked.',
  },
  {
    what: 'Cluster names must be unique across regions',
    severity: 'grey',
    detail:
      'Both existing rules clusters carry the SAME name, so neither could be joined to a global database with the other. Combined with the managed-password refusal above, this is why UC7 could not be stood up by reconfiguring what was already deployed: BOTH existing clusters are untouched — not modified, not restarted, not reconfigured — and UC7 runs on two new clusters of its own.',
  },
  {
    what: 'The seed path was destructive-idempotent, and the fix was a DEFAULT rather than a guard',
    severity: 'blue',
    detail:
      'The seed script dropped its tables before recreating them. Run naively against a global primary that DROP replicates to the secondary and wipes both regions in one command. Closed by making the non-destructive path the DEFAULT — a guard can be argued with, a default cannot — with an explicit rebuild flag that is REFUSED against any global-database member, membership DETECTED from the cluster rather than inferred from its name. ⚠️ What non-destructive does NOT do, stated rather than implied: an upsert converges the rows the seed names and cannot remove one it does not, so a hand-added rule survives and the live table becomes a SUPERSET of the file. Row counts are asserted rather than printed so that shows up as a failure.',
  },
  {
    what: 'A fifth Aurora cluster in the estate, and a standing cost nobody planned for',
    severity: 'blue',
    detail:
      'UC7 does not reuse the deployed rules cluster — sharing it would mean UC7 work could interrupt UC2, which is a live measurable option, and the constraint on this whole exercise is that UC1, UC2 and UC4 keep running. But because adoption was REFUSED (see the managed-password row) UC7 added TWO clusters rather than one, so its standing cost is roughly DOUBLE what the plan projected. The dollar figures are in the build log and the plan document where they can be checked against the live Price List API, and are deliberately not transcribed into this component.',
  },
];

/** Only the requirements UC7 actually changes. Verdicts, and what each rests on. */
const REQUIREMENTS = [
  {
    id: 'R7',
    text: 'Active-active multi-region at all layers',
    verdict: 'NOT MET in letter',
    tone: 'red',
    detail: UC7_R7_NOT_MET,
  },
  {
    id: 'R8',
    text: 'Zero outages, not even for patching',
    verdict: 'NOT MET under A-U1',
    tone: 'red',
    detail: UC7_DDL_NOT_FORWARDED,
  },
  {
    id: 'R14',
    text: 'Rules enforced correctly',
    verdict: 'improved, DEPLOYED',
    tone: 'green',
    detail:
      'The database enforces the constraints again instead of Lambda, and this is no longer a design intent: the deployed schema carries the foreign keys and CHECK constraints, every /v7 response reports referentialIntegrity: "database-enforced", and /v7 was proven to resolve all four constructs correctly in both regions — including the specificity tie-break row that only a correct ORDER BY in the resolving join can select, which is a real exercise of the join rather than a lookup. Whether the improvement is worth what it appears to be still depends on R14 itself: the customer says rules are ALREADY enforced at application level today.',
  },
  {
    id: 'R2',
    text: '120-day uniqueness per MID/STC',
    verdict: 'unchanged by UC7',
    tone: 'grey',
    detail:
      'UC1’s issuance is deliberately untouched: the DynamoDB high-water mark, the Valkey buffer with its atomic increment, the region-partitioned disjoint ranges, the mark committed before vending, and exhaustion refusing rather than wrapping. UC7 changes the RULES layer only. UC1’s zero-duplicate evidence is measured and is the reason UC7 is defensible at all — and it is also why UC7 inherits UC1’s two remaining duplicate paths as WORK rather than as accepted risk.',
  },
];

/**
 * WHAT BUILDING UC7 ACTUALLY ESTABLISHED, AND HOW.
 *
 * ⚠️ EVERY ROW HERE IS A STRUCTURAL FACT, NOT A PERFORMANCE FIGURE, and that separation is the
 * whole reason this table is distinct from the one below it. "The Data API cannot reach a secondary"
 * and "DDL is refused with SQLSTATE 25006" are claims a single command settles and that stay settled.
 * "UC7 is faster or slower than UC4" is not one of those, is not established, and is not here.
 *
 * The `cuts` column is not decoration either: three of these findings cut AGAINST UC7. Sorting a
 * findings table so the inconvenient rows sit at the bottom is how a page argues while appearing to
 * report, so the direction is a COLUMN and the rows that cut against are not last.
 */
const ESTABLISHED = [
  {
    finding: 'Which endpoint each region actually connected to',
    how: 'Reported by the running system from the TRANSPORT’s own field rather than from a configuration value — so the response says what the connection DID, not what the deploy INTENDED. us-east-1 reaches the primary’s cluster endpoint; us-west-2 reaches the secondary’s reader endpoint. Across many concurrent health calls per region, exactly one endpoint appeared in each region’s responses: no container in either region ever reached the other region’s member.',
    cuts: 'for',
    why: 'The local-read property is the entire point of a global database, so it is proven rather than assumed. ⚠️ And crossing the two regions’ endpoints would produce NO error, NO wrong answer and NO failed request — both members hold identical rows, so a mis-wired secondary would serve every request CORRECTLY while reading cross-region and reporting it as a local read. There is no response check that can catch that afterwards; it can only be prevented at deploy time, and the deploy refuses if the two regions resolve the same endpoint or the same secret.',
  },
  {
    finding: 'The secondary is genuinely a replica — asked OF THE DATABASE',
    how: 'pg_is_in_recovery() returns true on the secondary and false on the primary, and the question was put to the database rather than inferred from a hostname or an environment variable.',
    cuts: 'for',
    why: 'This is the lesson from an earlier failure in this project, where rules infrastructure was believed replicated for weeks because nobody asked the resource. If the secondary reported false, that endpoint would not be the secondary and every "local read" figure taken from it would actually be a cross-region read.',
  },
  {
    finding: 'A forwarded write executes on the PRIMARY — R7’s verdict, measured',
    how: 'Rows written THROUGH the us-west-2 secondary were then read back FROM the us-east-1 primary. On the secondary, pg_is_in_recovery() is true while transaction_read_only is off: the session is writable only because the forwarding shim makes it so.',
    cuts: 'against',
    why: 'A write ENDPOINT in us-west-2, a WRITER in us-east-1. This is the direct demonstration that R7 is NOT met in letter — the requirement is missed by the topology as deployed, not by a limitation someone read about.',
  },
  {
    finding: 'DDL through the secondary is REFUSED — the R8 case, evidenced',
    how: 'CREATE TABLE, ALTER TABLE, DROP TABLE and CREATE INDEX each refused with SQLSTATE 25006 and an explicit "not supported for write forwarding" reason, with forwarding enabled.',
    cuts: 'against',
    why: 'There is no per-region schema autonomy. A schema change cannot be applied from us-west-2 under ANY configuration of write forwarding — it must go to the primary and replicate. That is R8 "not even for patching", stated as a measured refusal rather than as a documentation quote.',
  },
  {
    finding: 'The RDS Data API cannot reach a secondary — not even for a read',
    how: 'A read, a bare SELECT 1 and a write all failed against the secondary while the IDENTICAL pattern succeeded against the primary. The Data API is enabled on both clusters.',
    cuts: 'against',
    why: 'This is the finding that did NOT simplify UC7. Had the Data API worked, the largest single piece of work in the whole option would have evaporated. It does not work, so a separate PostgreSQL-wire transport is REQUIRED — and its failure mode carries a name that reads transient while being permanent.',
  },
  {
    finding: 'Aurora accepts a SERVER-SIDE statement timeout; DSQL refuses the connection',
    how: 'Set live on both members and read back from the server. DSQL rejects the connection outright with SQLSTATE 0A000 when the same setting is sent.',
    cuts: 'for',
    why: 'A real if small advantage over UC4, found by driving the transport rather than by reading about it: query_timeout abandons the WAIT while the statement keeps consuming capacity, whereas statement_timeout CANCELS it.',
  },
  {
    finding: 'Aurora Global Database is incompatible with the RDS-managed master password',
    how: 'The API refuses outright, for a brand-new member as well as for adopting an existing cluster. Deterministic in both regions.',
    cuts: 'against',
    why: 'Adopting a deployed cluster would have meant deleting the managed secret UC2 currently authenticates with. So UC7 holds a self-managed credential with no automatic rotation, and it cost a second new cluster — roughly doubling its projected standing cost. This appears in NO planning document; it was found by attempting it.',
  },
  {
    finding: 'Only ONE copy of the resolving SQL exists, and the wire text was compared byte-for-byte',
    how: 'The three endpoints resolve to the SAME constant objects — reference identity, which is a stronger claim than string equality because it proves one definition rather than three that happen to agree today. The REWRITTEN text and parameter ORDER that each transport actually puts on the wire were then compared statement by statement, and both stores were driven against ONE shared database built from the real schema so they could not agree merely by having been seeded consistently.',
    cuts: 'for',
    why: 'A third copy of a rules query would drift, and a third copy of the ISSUANCE path is worse: a duplicate tracking number is the one failure this system cannot survive. /v7 adds neither — it shares UC2’s store class and the shared label path, and a test now COUNTS the copies so a third cannot appear silently.',
  },
];

/**
 * THE MEASUREMENTS, AND THE STATE EACH ONE IS ACTUALLY IN.
 *
 * ⚠️ THIS TABLE IS WHY "DEPLOYED" DID NOT BECOME "MEASURED". It used to be captioned "the list of
 * things nobody has run" with every row unrun and no results column, which was the plainest way to
 * say PLANNED. UC7 is no longer planned, so the caption changed — but the rows did not, because a
 * deployment does not measure itself. Each row now carries an explicit status, and the counts in the
 * header are DERIVED from these rows rather than typed, so a row that changes state cannot leave a
 * stale total behind it.
 *
 * ⚠️ `done` NOW EXISTS, AND ADDING IT WAS THE POINT OF THIS EDIT. This block used to say the
 * statuses were "deliberately only two ... there is no `done`, because none of these is done". That
 * was true when written and is now false: all eight rows were driven in the session recorded in
 * `loadtest/results/UC7-EVALUATION.md`, including the three that cannot be taken back cheaply — a
 * live minor-version engine patch, a managed switchover, and an `--allow-data-loss` failover.
 *
 * The counts in the header are DERIVED from the rows below, so flipping a status could not leave a
 * stale total behind it — which is exactly why the totals were safe to trust and the prose was not.
 * The words "0 complete" were typed next to the derived numbers and had to be corrected by hand.
 *
 * ⚠️ `done` MEANS MEASURED, NOT PASSED. Four of these rows record UC7 behaving WORSE than the
 * option it competes with, and §9 of the evaluation concludes against adopting it. A row is `done`
 * when a number exists, whatever that number says.
 */
const TEST_PLAN = [
  {
    test: 'Load test, both regions',
    status: 'done',
    why: 'Same shape as the runs behind the Load test tab, so UC7 can be compared rather than asserted.',
    control:
      'The two regions’ API Gateway account burst quotas USED TO DIFFER by an order of magnitude, with the lower one not adjustable via Service Quotas, so an uncontrolled cross-region comparison measured the QUOTA and not the design. That asymmetry was RESOLVED by an AWS support case and both regions were then verified symmetric, so this confound is gone and a comparison no longer has to be handicapped to the smaller bucket — which makes a genuinely fair cross-region run possible for the first time. Still control for Serverless v2 capacity warm-up from idle, transport differences, and cold starts — AND for the capacity ceiling, which was below the recommended minimum for a Serverless v2 global primary and should be raised BEFORE measuring rather than reasoned around afterwards.',
  },
  {
    test: 'Planned switchover, measured end to end',
    status: 'done',
    why: 'Including the instance restart in ALL regions, which is the part RPO 0 does not cover. Enabling write forwarding produced no observed restart and no status change, which is the CHEAP half of this question; the switchover is the expensive half and it is the one that matters.',
    control:
      'Duration depends on replication lag at the moment it starts, so lag has to be recorded alongside it or the number is unreproducible.',
  },
  {
    test: 'Unplanned failover, with its ACTUAL RPO',
    status: 'done',
    why: 'An unplanned failover requires explicitly allowing data loss. The RPO is non-zero and depends on lag at the instant of failure — so it must be observed, not quoted.',
    control:
      'Distinct from MANUAL promotion, which is the IRREVERSIBLE operation: a promoted cluster permanently stops replicating and can never rejoin that global database. Conflating the two mis-schedules the drill, because one can be repeated and the other cannot. No promotion, switchover or failover has been performed.',
  },
  {
    test: 'Replication lag UNDER LOAD',
    status: 'done',
    why: 'The AuroraGlobalDBReplicationLag metric was confirmed to be EMITTED for this topology — in the secondary’s region only — and lag was observed at rest by an in-band probe comparing database-server clocks at both ends. What has NOT happened is observing it while a load run is in flight.',
    control:
      'A lag figure from an idle cluster tells you nothing about the switchover it will gate, so the at-rest observation must not be quoted as the answer. Two further controls the at-rest run already exposed: a naive "insert then poll from the other region" measures Lambda cold start and VPC attach rather than replication, and the per-minute metric AVERAGE and MAXIMUM differ by an order of magnitude because one averages over a mostly-idle minute — neither may be quoted without saying which it is.',
  },
  {
    test: 'A rules WRITE issued from the secondary region',
    status: 'done',
    why: 'DONE with forwarding ON: the write was accepted through the secondary and then read back from the primary, and the session consistency mode was confirmed live as the documented default. NOT done: the same write with forwarding OFF, so the failure mode of the disabled configuration is still inferred rather than observed.',
    control:
      'Also exercise the consistency mode actually chosen for forwarding — the default is not the only option and they do not behave alike. Read-after-write in the SAME session works, and that is a session guarantee and NOT a cross-container one, so it does not close the hole it resembles.',
  },
  {
    test: 'Resolving-query latency, local and cross-region',
    status: 'done',
    why: 'A secondary serves LOCAL reads; the primary is a cross-region hop away for writes. Both paths need measuring separately.',
    control:
      'The measured cross-region penalty on the current system consumes most of the tightest stated budget on its own — which is why this is a measurement and not an estimate. Client-side samples taken from one laptop during the build are NOT this measurement and must not be quoted as it: they are dominated by where the laptop is, which is why the further region looked faster.',
  },
  {
    test: 'A live minor-version patch under sustained load',
    status: 'done',
    why: 'THE R8 CASE. It is unmeasured for EVERY option on this site today, including the deployed ones, which makes it the single most valuable measurement available.',
    control:
      'Managed switchover and failover require primary and secondary on the same major AND minor version, so the patch ORDER is itself part of the test. Automatic minor upgrade is deliberately OFF on both members: it has no effect on global-database members anyway, and leaving it on would imply a per-region autonomy that does not exist — which is the R8 case restated as a configuration fact.',
  },
  {
    test: 'Re-measure UC4 alongside UC7',
    status: 'done',
    why: 'Rather than comparing UC7 against stale UC4 numbers taken at a different time against different connection states.',
    control:
      'This is a statement of method, not an optional extra: a comparison against numbers gathered under other conditions is not a comparison. The transports’ connection recycle intervals were deliberately MATCHED between /v4 and /v7 for this reason — a different interval would be a second uncontrolled variable in a comparison whose only purpose is to isolate the engine.',
  },
];

/** Derived, never typed: a row that changes state cannot leave a stale total behind it. */
const TESTS_NOT_RUN = TEST_PLAN.filter((t) => t.status === 'not run').length;
const TESTS_PARTIAL = TEST_PLAN.filter((t) => t.status === 'partial').length;
const TESTS_DONE = TEST_PLAN.filter((t) => t.status === 'done').length;

/**
 * WHAT UC7 INHERITS FROM UC1 AND MUST TREAT AS WORK.
 *
 * UC1's issuance stays exactly as deployed — which is the point of UC7 — and that means UC7 also
 * inherits the two duplicate paths that a full redesign would have deleted structurally. Listing
 * them as WORK rather than as accepted risk is the difference between a plan and a wish: a gap is
 * acceptable, a duplicate is catastrophic, and any duplicate path disqualifies the option.
 */
const INHERITED = [
  {
    path: 'Buffer restored from a snapshot',
    status: 'closable by policy',
    tone: 'blue',
    detail:
      'A buffer restored from a snapshot re-vends serials below the high-water mark, and the conditional write is NEVER EVEN CONSULTED — so the usual last line of defence does not engage. Automatic snapshots are now explicitly disabled, and the service had assigned itself a daily snapshot time when none was specified, so "unmentioned" was not the same as "off". The residual exposure is a DELIBERATE manual restore, which an IAM deny can close.',
  },
  {
    path: 'Cursor loss with no snapshot involved',
    status: 'needs a guard on the vend path',
    tone: 'red',
    detail:
      'Losing the buffer’s cursor key alone re-vends from the range base, with no snapshot anywhere in the story. An IAM deny does NOT close this. The obvious guard — refuse a window sitting below the high-water mark — was found NOT IMPLEMENTABLE, because a healthy mid-block buffer and a rolled-back one are bit-identical in cursor, end and mark. A genuinely different mechanism is required, and getting it right is in scope for UC7 rather than assumed. ⚠️ Being DEPLOYED does not close this: /v7 changes the rules layer only, and the serial is still vended from the region’s own buffer and its own high-water mark exactly as it is for UC1, UC2 and UC4.',
  },
];

/**
 * Takes the SAME props as Uc4Panel now, and that change is the whole point of this revision.
 *
 * It used to take none, on the stated grounds that "UC7 calls nothing, so it is handed nothing".
 * `/v7` is deployed and vends, so it calls something and needs the endpoint and the live flag. The
 * old comment also said a future edit wanting an endpoint here "has to first deploy a cluster for it
 * to point at" — which is precisely what happened, in that order.
 */
export default function Uc7Panel({ mode, endpoints, live, liveProbe }) {
  // Defaults to globex/PRIORITY rather than acme, because globex resolves C07 through a
  // customer-specific rule with a 6-digit mailer ID — the most discriminating of the four
  // constructs, so the first thing a viewer presses exercises the join hardest.
  const [customer, setCustomer] = useState(optionFor(CUSTOMER_OPTIONS, 'globex'));
  const [service, setService] = useState(DEFAULT_SERVICE);
  const [weightOz, setWeightOz] = useState('12');
  const [destZip, setDestZip] = useState('20260');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  /**
   * THE ENGINE ASSERTION. This is the load-bearing part of the vend control, and it matters MORE
   * here than the identical assertion does on UC4.
   *
   * A `/v4` response that had really come from Aurora PostgreSQL would eventually give itself away —
   * different dialect quirks, different error text, a missing retry counter. `/v7` has no such tell:
   * it runs the SAME store class over the SAME engine version against the SAME seeded rows as UC2,
   * so a `/v7` answered by UC2's INDEPENDENT cluster would return a completely plausible barcode
   * with a correct construct and a correct service type code. It would be indistinguishable by
   * inspection. The only thing that can discriminate is the response saying which topology it came
   * from, and the API is built so it cannot lie by omission: there is no fallback branch, an
   * unreachable global database answers 503 rather than quietly serving from somewhere else.
   *
   * So an unexpected `engine` is surfaced as an ERROR and never rendered. Naming the value received
   * matters: `aurora-postgresql` would mean UC2's Data API path answered and `aurora-dsql` would
   * mean UC4's did, and those are different diagnoses.
   */
  function assertGlobalPostgres(payload) {
    const got = payload ? payload.engine : undefined;
    if (got !== UC7_ENGINE) {
      throw new Error(
        `the response did not report engine "${UC7_ENGINE}" (got ${JSON.stringify(got)}), so this ` +
          'did NOT prove the Aurora Global Database answered — refusing to present it as a UC7 ' +
          'result. This assertion is not a formality: /v7 and /v2 run the same store class over the ' +
          'same PostgreSQL version against the same rows, so a response from UC2’s independent ' +
          'cluster would look completely correct and would be indistinguishable by inspection.' +
          (got === 'aurora-postgresql'
            ? ' The value received is UC2’s Data API path against an INDEPENDENT cluster, which is exactly the substitution this check exists to catch.'
            : got === 'aurora-dsql'
              ? ' The value received is UC4’s engine, so this request reached the DSQL path.'
              : '')
      );
    }
  }

  /**
   * The role assertion, which is separate from the engine assertion on purpose.
   *
   * The engine field says WHICH TOPOLOGY answered. `globalRole` says WHERE IN IT — and the two are
   * different claims: a global-database member is either the single primary or a secondary, and a
   * response reporting neither cannot support the primary-versus-secondary statement this panel
   * makes beside it. An unrecognised value is refused rather than rendered verbatim, because the
   * whole point of showing the role is that R7's verdict depends on which one answered.
   */
  function assertRole(payload) {
    const role = payload ? payload.globalRole : undefined;
    if (role !== 'primary' && role !== 'secondary') {
      throw new Error(
        `the response reported engine "${UC7_ENGINE}" but no recognisable globalRole (got ` +
          `${JSON.stringify(role)}). A global-database member is either the single primary or a ` +
          'secondary, and without knowing which one answered the primary-vs-secondary claim on ' +
          'this tab cannot be made — so this is reported rather than rendered.'
      );
    }
  }

  /**
   * Live-only, with NO local-render fallback, for exactly the reason Uc4Panel gives.
   *
   * UC1's local mode is honest because it demonstrates barcode CONSTRUCTION, which is arithmetic the
   * browser really can do. UC7's entire claim is that a specific TOPOLOGY resolved the rules, and a
   * JavaScript stand-in would render a barcode under a heading naming a global database that was
   * never involved. That is a worse failure here than anywhere else on the site, because the
   * plausible-looking wrong answer is the failure mode this whole tab guards against.
   */
  function requireLive(what) {
    if (!live) {
      throw new Error(
        `${what} needs the live backend: UC7's claim is that the Aurora PostgreSQL Global Database ` +
          'answered, and a local stand-in could not demonstrate that.' +
          (liveProbe && liveProbe.reason ? ` ${liveProbe.reason}` : '')
      );
    }
  }

  /** POST /v7/labels — UC2's resolving join over the global database, then UC1's serial path. */
  async function vendViaGlobalDb() {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      requireLive('The UC7 vend');
      const zipBad = zipProblem(destZip);
      if (zipBad) throw new Error(zipBad);
      const w = parseWeightOz(weightOz);
      if (w.error) throw new Error(w.error);

      const res = await timedFetch(
        `${endpoints.rulesApi}/v7/labels`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            customerId: customer.value,
            serviceCode: service.value,
            destZip: destZip.trim(),
            weightOz: w.oz,
          }),
        },
        20000
      );
      if (!res.ok) throw new Error(errorText(res));
      const payload = res.json || {};
      assertGlobalPostgres(payload);
      assertRole(payload);
      let barcode = null;
      let barcodeError = null;
      if (payload.label && payload.label.pic) {
        try {
          barcode = barcodeFromApiItem(payload.label);
        } catch (err) {
          barcodeError = err.message;
        }
      }
      setResult({ ms: res.ms, payload, barcode, barcodeError, placeholder: isPlaceholder(payload) });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  // What the current pair is EXPECTED to resolve to, from observed live behaviour. Shown as an
  // expectation the round trip then confirms or refutes — never as the result itself. Shared with
  // UC2/UC4 rather than copied: the three endpoints resolve the same rows through the same query.
  const expected = OBSERVED_RESOLUTIONS.find(
    (r) => r.customerId === customer.value && r.serviceCode === service.value
  );
  const bandNow = bandForOz(Number(weightOz));

  return (
    <SpaceBetween size="l">
      {/*
        THE STATUS NOTICE, FIRST, AND THE HEADLINE HAS NOW BEEN WRONG TWICE IN THE SAME DIRECTION.

        Round one it read "UC7 IS PLANNED, NOT BUILT" (`error`), which went false when UC7 was built.
        Round two it read "DEPLOYED ... AND IT IS NOT MEASURED" (`warning`), which went false when the
        evaluation ran. Both times the page kept rendering perfectly and every presence assertion kept
        passing while the top line stated something untrue — which is the failure mode this panel's own
        caveat header warns about, committed twice by the file that warns about it.

        ⚠️ THE TONE STAYS `warning`, AND THE REASON CHANGED. It is no longer "unmeasured"; it is
        "measured, and the measurements argue AGAINST this option". A reader skimming for a
        screenshot is now most likely to lose the REJECTION rather than the absence of data, so the
        header states the outcome — deployed, measured, NOT chosen — rather than the status.

        ⚠️ IT IS NOT `success` OR `info`. UC7 wins the resolving read outright and loses no acked
        write, so a green tone is available and would be genuinely misleading: the two flaws that
        decided against it are intrinsic, and a tone that reads as endorsement would invite exactly
        the adoption the evaluation concludes against.
      */}
      <Alert
        type="warning"
        header="UC7 IS DEPLOYED, MEASURED — AND WAS NOT CHOSEN"
        data-testid="uc7-measured-and-not-chosen"
      >
        <SpaceBetween size="xs">
          <Box variant="p">{UC7_MEASURED_AND_NOT_CHOSEN}</Box>
          <Box variant="p" data-testid="uc7-outages-measured">
            {UC7_OUTAGES_MEASURED_AND_NOT_OUR_DEFECT}
          </Box>
          <Box variant="p" data-testid="uc7-intrinsic-vs-surmountable">
            {UC7_INTRINSIC_VS_SURMOUNTABLE}
          </Box>
          {/*
            These two protect the losing option from being reported unfairly, which matters more
            than protecting the winner: the connection cliff was OUR artefact at a stale ACU band
            and not an Aurora ceiling, and UC4's R8 advantage is an UNMEASURED UNKNOWN rather than
            a measured zero — DSQL exposes no schedulable patch or failover to drive.
          */}
          <Box variant="p" data-testid="uc7-connection-cliff-artefact">
            {UC7_CONNECTION_CLIFF_WAS_OUR_ARTEFACT}
          </Box>
          <Box variant="p" data-testid="uc7-measured-cost-vs-unknown">
            {MEASURED_COST_VS_UNMEASURED_UNKNOWN}
          </Box>
          <Box variant="p" data-testid="uc7-role-forwarding-fixed">
            {UC7_ROLE_AND_FORWARDING_DEFECTS_FIXED}
          </Box>
          <Box variant="p">
            <strong>
              There is now a vend button on this tab, and what it must prove is harder than on any
              other tab.
            </strong>{' '}
            Every option tab has one because a button means a real round trip against a real engine
            that has to prove which engine answered. Here that proof carries more weight:{' '}
            <code>/v7</code> and <code>/v2</code> run the <em>same</em> store class over the{' '}
            <em>same</em> PostgreSQL version against the <em>same</em> rows, so a <code>/v7</code>{' '}
            answered by UC2&apos;s independent cluster would return a completely plausible barcode
            and would be <strong>indistinguishable by inspection</strong>. So this panel refuses any
            response that does not report <code>engine: &quot;{UC7_ENGINE}&quot;</code>, and it shows{' '}
            <code>globalRole</code> so you can see whether the primary or a secondary served it.
          </Box>
          <Box variant="p">
            <strong>R7 is still NOT met in letter, and being deployed did not change that.</strong>{' '}
            If anything it is now stated more strongly, because the verdict below is no longer read
            from documentation — it was demonstrated by writing through the secondary and reading the
            row back from the primary.
          </Box>
        </SpaceBetween>
      </Alert>

      {/*
        VEND THROUGH THE GLOBAL DATABASE. Live-only by design, modelled on Uc4Panel — see
        vendViaGlobalDb() and assertGlobalPostgres() above for why there is no local fallback and why
        the engine assertion is load-bearing rather than decorative.
      */}
      <Container
        header={
          <Header
            variant="h2"
            description="POST /v7/labels — UC2's resolving join, unmodified and shared rather than re-implemented, executed over the Aurora PostgreSQL Global Database through a PostgreSQL-wire transport, then the SAME UC1 serial path (DynamoDB mark + Valkey buffer)."
          >
            Vend a label through the Aurora Global Database
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/*
            NO CONSTRUCT PICKER HERE EITHER, for the reason rules-catalog.js states: in /v7 the
            construct is the rules' ANSWER, so a dropdown would let a viewer override the very thing
            the tab exists to demonstrate. Changing the CUSTOMER moves the construct honestly,
            because the data moves rather than the caller asking.
          */}
          <ColumnLayout columns={4}>
            <FormField
              label="Customer"
              description="the four customers seeded into the global primary from the UNMODIFIED schema — changing this changes the RESOLVED construct"
            >
              <Select
                selectedOption={customer}
                onChange={({ detail }) => setCustomer(detail.selectedOption)}
                options={CUSTOMER_OPTIONS}
                data-testid="uc7-customer"
              />
            </FormField>
            <FormField label="Service" description="must be an ACTIVE subscription for this customer">
              <Select
                selectedOption={service}
                onChange={({ detail }) => setService(detail.selectedOption)}
                options={SERVICE_OPTIONS}
                data-testid="uc7-service"
              />
            </FormField>
            <FormField
              label="Weight (oz)"
              description={bandNow ? `selects the weight band — ${bandNow}` : 'selects the weight band'}
            >
              <Input
                value={weightOz}
                type="number"
                onChange={({ detail }) => setWeightOz(detail.value)}
                data-testid="uc7-weight"
              />
            </FormField>
            <FormField label="Destination ZIP" description={ZIP_DESCRIPTION}>
              <Input
                value={destZip}
                onChange={({ detail }) => setDestZip(detail.value)}
                data-testid="uc7-zip"
              />
            </FormField>
          </ColumnLayout>

          {expected ? (
            <Box variant="small" color="text-body-secondary">
              <StatusIndicator type="pending">
                expected: rule <code>{expected.rule}</code> → STC <code>{expected.stc}</code> /
                construct <code>{expected.construct}</code> — an expectation from a previous live
                check, not a result. The vend below either confirms it or contradicts it.
              </StatusIndicator>
            </Box>
          ) : (
            <Box variant="small" color="text-body-secondary">
              <StatusIndicator type="info">
                this customer/service pair has no recorded expectation — it may be refused by the
                rules (a suspended customer, a retired service, or a missing subscription), which is a
                demonstration of enforcement rather than a bug. Here the refusal comes from a real
                foreign key and a real CHECK constraint rather than from application code.
              </StatusIndicator>
            </Box>
          )}

          <Box textAlign="right">
            <Button
              variant="primary"
              loading={busy}
              onClick={vendViaGlobalDb}
              data-testid="uc7-submit"
            >
              Vend via Aurora Global Database
            </Button>
          </Box>

          {error ? (
            <Alert
              type="error"
              header="The UC7 vend did not succeed, and is not being presented as if it had"
              data-testid="uc7-error"
            >
              {error}
            </Alert>
          ) : null}

          {result ? (
            <SpaceBetween size="s">
              <Alert
                type="success"
                header={`Resolved by the Aurora Global Database (${result.payload.globalRole}) in ${Math.round(result.ms)} ms`}
                data-testid="uc7-result"
              >
                <SpaceBetween size="xs">
                  <Box variant="p">
                    The response reported <code>engine: &quot;{UC7_ENGINE}</code>
                    <code>&quot;</code>, which is what makes this a global-database result rather
                    than a barcode that merely looks right — and the assertion matters here in a way
                    it does not elsewhere, because UC2 would have produced an equally plausible one.
                    Rule{' '}
                    <strong>
                      {result.payload.rulesApplied
                        ? (result.payload.rulesApplied.find((r) => r.selected) || {}).name || 'n/a'
                        : 'n/a'}
                    </strong>{' '}
                    selected STC{' '}
                    <strong>
                      {result.payload.label ? result.payload.label.serviceTypeCode : 'n/a'}
                    </strong>{' '}
                    / construct{' '}
                    <strong data-testid="uc7-construct">
                      {result.payload.label ? result.payload.label.construct : 'n/a'}
                    </strong>
                    . Nothing in the request named that construct — it is what the rules chose.
                  </Box>
                  {/*
                    THE ROLE, THE ENDPOINT AND THE WRITER, READ FROM THE RESPONSE.

                    `readEndpoint` is reported by the TRANSPORT rather than from a configuration
                    value, so it says what the connection DID and not what the deploy INTENDED. That
                    distinction is the only defence against the one misconfiguration that cannot be
                    caught after the fact: both members hold identical rows, so a region wired to
                    the other region's member would serve every request CORRECTLY while reading
                    cross-region and calling it a local read.
                  */}
                  <KeyValuePairs
                    columns={3}
                    items={[
                      {
                        label: 'globalRole — which member answered',
                        value: (
                          <Badge color={result.payload.globalRole === 'primary' ? 'green' : 'blue'}>
                            {String(result.payload.globalRole)}
                          </Badge>
                        ),
                      },
                      {
                        label: 'writer — reported by the API itself',
                        value: <code>{String(result.payload.writer)}</code>,
                      },
                      {
                        label: 'referentialIntegrity',
                        value: <code>{String(result.payload.referentialIntegrity)}</code>,
                      },
                      {
                        label: 'readEndpoint — from the transport, not from config',
                        value: <code>{String(result.payload.readEndpoint)}</code>,
                      },
                    ]}
                  />
                  <Box variant="p" data-testid="uc7-role-note">
                    {result.payload.globalRole === 'secondary' ? (
                      <>
                        <strong>A secondary answered this request.</strong> Its reads are LOCAL, and
                        that is the property a global database exists for. But its{' '}
                        <code>writer</code> field above names the <em>other</em> region, which is
                        R7&apos;s verdict in the running system&apos;s own words: a write{' '}
                        <em>endpoint</em> here, a <em>writer</em> there.
                      </>
                    ) : (
                      <>
                        <strong>The single primary answered this request.</strong> There is exactly
                        one writer in this topology and this is it — which is why a request served
                        from the other region reports a <code>writer</code> naming this one rather
                        than itself. Switch the endpoint on the <strong>Endpoints &amp; mode</strong>{' '}
                        tab to the second region to see the secondary answer.
                      </>
                    )}
                  </Box>
                </SpaceBetween>
              </Alert>

              {/*
                The resolution block: the evidence that a JOIN produced this rather than a lookup —
                the customer tier, the weight band the ounces landed in, and the mailer ID the
                customer row supplied. Those come from different tables, joined by the database.
              */}
              {result.payload.resolution ? (
                <KeyValuePairs
                  columns={4}
                  items={Object.entries(result.payload.resolution).map(([k, v]) => ({
                    label: k,
                    value: <code>{String(v)}</code>,
                  }))}
                />
              ) : null}

              {result.barcode ? (
                <SerialCard
                  barcode={result.barcode}
                  index={null}
                  mode={mode}
                  extra={{ live: true, requestId: result.payload.requestId }}
                />
              ) : result.barcodeError ? (
                <Alert type="warning" header="The barcode returned did not parse">
                  {result.barcodeError}
                </Alert>
              ) : null}
              <RawJson value={result.payload} />
            </SpaceBetween>
          ) : null}
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            description="UC1's serial issuance stays EXACTLY as deployed. Only the rules layer changes: Aurora DSQL becomes Aurora PostgreSQL Global Database with global write forwarding — deployed, vending, and now fully measured against UC4."
          >
            What UC7 is
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/*
            TWO STATUS CELLS RATHER THAN ONE, because the distinction cannot fit in one badge. One
            reading "deployed" alone would be read as "done"; one reading "measured" alone would be
            read as "passed". Both, side by side, are the claim.

            ⚠️ THE MEASUREMENT BADGE SAID "NOT measured — no load test, no failover drill", AND THAT
            WENT FALSE. All eight measurements ran: the load test, a live minor-version engine patch,
            a managed switchover and an --allow-data-loss failover, with a caller polling both
            regions at 250 ms. A red badge asserting the opposite, beside a green "vending live"
            badge, told a customer the single most load-bearing thing about this option was untested.

            It stays RED, and that is deliberate. The colour now carries the VERDICT rather than the
            absence of data: UC7 was measured and rejected. Green would read as endorsed, and grey
            would read as unremarkable — but the outcome here is a rejection on availability grounds,
            which is exactly the kind of thing a skimming reader must not miss.
          */}
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Deployment status</Box>
              <Box>
                <Badge color="green">deployed — vending live in both regions</Badge>
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Measurement status</Box>
              <Box>
                <Badge color="red">MEASURED — and NOT CHOSEN on availability</Badge>
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Rules store</Box>
              <Box>
                Aurora PostgreSQL Global Database — one primary in us-east-1, one secondary in
                us-west-2, with global write forwarding enabled
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Serial issuance</Box>
              <Box>Unchanged from UC1 — DynamoDB mark + Valkey buffer</Box>
            </div>
          </ColumnLayout>

          <Alert
            type="info"
            header="Referential integrity is back in the database — and the customer says it was never out of it"
            data-testid="uc7-referential-integrity"
          >
            <Box variant="p">{UC7_REFERENTIAL_INTEGRITY_DB_ENFORCED}</Box>
          </Alert>

          <Box variant="p">
            The changed part is the rules layer and nothing else. UC1&apos;s issuance —{' '}
            high-water mark in DynamoDB, an ElastiCache Valkey buffer with an atomic increment on
            the warm path, disjoint serial ranges partitioned per region, the mark committed{' '}
            <em>before</em> a serial is vended, and exhaustion refusing rather than wrapping — is
            deliberately untouched. Its measured zero-duplicate record is the reason UC7 is
            defensible at all, so redesigning it would throw away the evidence.
          </Box>

          <Alert type="info" header="There is no epoch registry, and that is why UC7 is not the option that failed">
            <Box variant="p">
              UC1&apos;s region disjointness is <strong>configured</strong>, not coordinated. There
              is no shared chain and no cross-region write to serialise, so nothing in this design
              needs <code>SERIALIZABLE</code> isolation — which write forwarding does not support,
              and which is what killed the earlier variant of this idea. Aurora PostgreSQL serves
              rules, and only rules.
            </Box>
          </Alert>

          <Alert type="info" header="Why this workload suits write forwarding at all">
            <SpaceBetween size="xs">
              <Box variant="p">
                The rules layer is <strong>read on every label request and written rarely</strong> —
                the customer recalls one barcode-format change in a couple of years, with new
                mailer-ID creation more frequent than that. A store that is read constantly and
                written a handful of times can afford a coordination cost on writes that a
                frequently-written store cannot.
              </Box>
              <Box variant="p">
                <strong>⚠️ The write RATE is still unconfirmed (U6)</strong> and it does real work in
                that argument. It is an open question, not an assumption with a number attached.
              </Box>
            </SpaceBetween>
          </Alert>
        </SpaceBetween>
      </Container>

      {/*
        GAINS WITH THEIR COUNTER-ARGUMENTS IN THE SAME ROW, not in a later section. A gains list
        whose objections live further down the page is read as a gains list.
      */}
      <Container
        header={
          <Header
            variant="h2"
            description="What UC7 gains over UC4 — each with the strongest argument against it in the same row, because a gains list with the objections further down the page is a sales sheet. The gains are no longer conditional now that UC7 is deployed, and neither are the objections."
          >
            What UC7 gains over UC4
          </Header>
        }
      >
        <Table
          variant="embedded"
          wrapLines
          items={GAINS.map((g, i) => ({ ...g, key: `${g.what}-${i}` }))}
          trackBy="key"
          data-testid="uc7-gains-table"
          columnDefinitions={[
            { id: 'what', header: 'Gain', cell: (g) => <Box fontWeight="bold">{g.what}</Box>, width: 260 },
            { id: 'detail', header: 'What it actually buys', cell: (g) => g.detail, minWidth: 320 },
            { id: 'against', header: 'The argument against it', cell: (g) => g.against, minWidth: 300 },
          ]}
        />
      </Container>

      {/*
        THE COSTS. Ordered severity-first rather than by topic: the single-writer row is not a
        trade-off to be absorbed, it is a stated requirement missed, and it must be the first thing
        read in this table.

        ⚠️ TWO OF THESE ROWS ARE THE REASON THIS TAB IS NOT ADVOCACY. The managed-password
        incompatibility and the master-credential row were both discovered by BUILDING UC7, appear in
        no planning document, and cut against it. Building an option and publishing only what it
        gained would make the build a marketing exercise.
      */}
      <Container
        header={
          <Header
            variant="h2"
            description="None of this may be softened, and building UC7 ADDED to it rather than only subtracting. The red rows decide the option: the first is a stated requirement MISSED rather than a trade-off absorbed, and two of the others were found by attempting the build and were predicted by nobody."
          >
            What UC7 gives up — including two findings that cut AGAINST it
          </Header>
        }
      >
        <Table
          variant="embedded"
          wrapLines
          items={COSTS.map((c, i) => ({ ...c, key: `${c.what}-${i}` }))}
          trackBy="key"
          data-testid="uc7-costs-table"
          columnDefinitions={[
            {
              id: 'what',
              header: 'Cost',
              cell: (c) => (
                <SpaceBetween size="xxs">
                  <Box fontWeight="bold">{c.what}</Box>
                  <Badge color={c.severity}>
                    {c.severity === 'red' ? 'decides the option' : c.severity === 'blue' ? 'consequence' : 'constraint'}
                  </Badge>
                </SpaceBetween>
              ),
              width: 300,
            },
            { id: 'detail', header: 'Stated plainly', cell: (c) => c.detail, minWidth: 420 },
          ]}
        />
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            description="Only the requirements UC7 changes. R7's and R8's verdicts are MEASURED against the deployed topology rather than read from documentation — but a requirement verdict is not a latency or throughput figure, and must not be quoted as one. The performance figures do exist; they are in the measured-results table on this tab, derived from the committed per-sample probe output."
          >
            Against the stated requirements
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/*
            THE ATTRIBUTION LEGEND, ALWAYS VISIBLE, ABOVE THE TABLE IT EXPLAINS.

            ⚠️ WHY THIS IS NOT IN A COLLAPSED SECTION OR A HEADER DESCRIPTION. The strongest verdict
            on this tab is "NOT MET under A-U1", and A-U1 is OUR OWN ASSUMPTION about what the
            customer meant by "zero outages" — not something the customer said. A reader who takes
            A-U1 for a customer requirement reads that verdict as "UC7 fails a USPS requirement",
            which is a materially different and unfair claim. The distinction has to be adjacent to
            the badge that depends on it, and visible without a click.

            The R/A split is stated in docs/greenfield/CONTEXT.md, which is also where A-U1's
            deliberate choice of the STRICTER reading is recorded. No later compression pass may
            reduce this to "assumptions apply" or "see assumptions". If it must shrink, the surviving
            sentence is the first one: R-numbers are the customer's, A-numbers are ours.
          */}
          <Box variant="p" data-testid="uc7-attribution-legend">
            <strong>How to read the identifiers below:</strong> R-numbers (R2, R7, R8, R14) are the{' '}
            <strong>customer&apos;s own stated requirements</strong>. A-U1 and A-U2 are{' '}
            <strong>OUR ASSUMPTIONS, not the customer&apos;s words</strong> — A-U1 is our reading of
            what &quot;zero outages&quot; means (zero unavailability from the caller&apos;s point of
            view, deliberately the stricter of the two readings), and A-U2 is our assumption that no
            compliance or authorisation boundary applies. Both are pending the customer&apos;s
            answer. So a verdict of &quot;NOT MET under A-U1&quot; below is measured against{' '}
            <strong>our interpretation</strong>, not against something USPS stated — and if
            &quot;zero outages&quot; instead means zero <em>issuance</em> outages, that verdict
            changes.
          </Box>
          <Box variant="p" data-testid="uc7-r7-verdict">
            <strong>The headline verdict: {UC7_R7_NOT_MET}</strong>
          </Box>
          <Table
            variant="embedded"
            wrapLines
            items={REQUIREMENTS}
            trackBy="id"
            data-testid="uc7-requirements-table"
            columnDefinitions={[
              { id: 'id', header: 'Req', cell: (r) => <Box fontWeight="bold">{r.id}</Box>, width: 70 },
              { id: 'text', header: 'Requirement', cell: (r) => r.text, width: 240 },
              {
                id: 'verdict',
                header: 'Verdict',
                cell: (r) => <Badge color={r.tone}>{r.verdict}</Badge>,
                width: 200,
              },
              { id: 'detail', header: 'What the verdict rests on', cell: (r) => r.detail },
            ]}
          />
        </SpaceBetween>
      </Container>

      {/*
        WHAT BUILDING IT ESTABLISHED. This container is NEW and it exists because "deployed" is only
        a meaningful claim if you can say what the deployment settled — otherwise it is a status
        badge. Every row is a structural fact from driving the real topology, and NOT ONE is a
        performance figure. The `Cuts` column keeps the direction visible: three of these findings go
        against UC7, and a findings table that reads as uniformly favourable is not a findings table.
      */}
      <Container
        header={
          <Header
            variant="h2"
            counter={`(${ESTABLISHED.length})`}
            description="Every row was settled by DRIVING the live topology rather than by reading about it — and not one row is a performance figure. Three of them cut AGAINST UC7, which is why the direction is a column rather than a matter of tone."
          >
            What building UC7 actually established
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Table
            variant="embedded"
            wrapLines
            items={ESTABLISHED.map((e, i) => ({ ...e, key: `${e.finding}-${i}` }))}
            trackBy="key"
            data-testid="uc7-established-table"
            columnDefinitions={[
              {
                id: 'finding',
                header: 'Finding',
                cell: (e) => (
                  <SpaceBetween size="xxs">
                    <Box fontWeight="bold">{e.finding}</Box>
                    <Badge color={e.cuts === 'against' ? 'red' : 'green'}>
                      {e.cuts === 'against' ? 'cuts AGAINST UC7' : 'cuts for UC7'}
                    </Badge>
                  </SpaceBetween>
                ),
                width: 300,
              },
              { id: 'how', header: 'How it was established', cell: (e) => e.how, minWidth: 320 },
              { id: 'why', header: 'Why it matters', cell: (e) => e.why, minWidth: 340 },
            ]}
          />
          <Alert type="info" header="None of the above is a latency, a throughput or a failover time">
            <Box variant="p">
              These are <strong>structural facts</strong> — the kind a single command settles and
              that stay settled. &quot;The Data API cannot reach a secondary&quot; and &quot;DDL is
              refused with SQLSTATE 25006&quot; are of that kind.{' '}
              <strong>&quot;UC7 is faster or slower than UC4&quot; is not</strong> — that one needed
              driving, and the figures it produced are in the measured-results table below, derived
              at build time from the committed per-sample probe output rather than typed here.
            </Box>
          </Alert>
        </SpaceBetween>
      </Container>

      {/*
        THE MEASURED RESULTS, RENDERED FROM THE DERIVED ARTEFACT AND NOT TYPED.

        Every figure below comes from `EVAL` — built by web/uc7-eval-summary.mjs from the committed
        per-sample probe JSON under loadtest/results/uc7-eval/. Nothing here is retyped from the
        evaluation report, because a hand-copied figure from this site reached a customer's own
        written analysis once already and that is the specific accident this indirection prevents.

        ⚠️ THE us-west-2 FAILOVER FIGURE IS A LOWER BOUND, AND RENDERING IT AS A POINT VALUE WOULD
        UNDERSTATE THE WORST OUTAGE THIS SYSTEM PRODUCED BY 2.09x. The probe was STILL FAILING when
        observation ended, so 569,136 ms is a floor, not a duration; the true ~1,189,149 ms was
        established afterwards from Aurora's own events. The parser cannot know that — it only knows
        it stopped watching while still failing — so it carries `stillFailing` and the table renders
        the bound from THAT FLAG rather than from a caveat someone has to remember to type. A future
        re-run that observes recovery will drop the bound automatically.
      */}
      <Container
        header={
          <Header
            variant="h2"
            description="Derived at build time from the committed per-sample probe output. The resolving read is UC7's clearest win; the topology events are why it still lost."
          >
            Measured results — UC7 against UC4, in-region
          </Header>
        }
      >
        <SpaceBetween size="m">
          <StatusIndicator type="success" data-testid="uc7-abba-result">
            <strong>
              Resolving read, us-east-1: /v7 p50 {EVAL.abba.use1.arms.v7.p50} ms vs /v4 p50{' '}
              {EVAL.abba.use1.arms.v4.p50} ms
            </strong>{' '}
            over {EVAL.abba.use1.samplesTotal.toLocaleString()} samples in an A/B/B/A design
            {EVAL.abba.use1.v7FasterInEveryBlock
              ? ', and /v7 was faster in EVERY block — not just on the aggregate'
              : ''}
            . UC7 wins this outright, which is exactly why the rejection rests on the topology events
            rather than on latency.
          </StatusIndicator>
          <Table
            variant="embedded"
            wrapLines
            data-testid="uc7-measured-events"
            trackBy="key"
            items={[
              { key: 'patch', label: 'Live minor-version engine patch', ev: EVAL.events.patch },
              { key: 'switchover', label: 'Managed switchover (RPO 0)', ev: EVAL.events.switchover },
              {
                key: 'failover',
                label: 'Unplanned failover (--allow-data-loss)',
                ev: EVAL.events.failover,
              },
            ]}
            columnDefinitions={[
              { id: 'ev', header: 'Topology event', cell: (r) => <strong>{r.label}</strong>, width: 260 },
              {
                id: 'use1',
                header: 'us-east-1 caller-visible outage',
                cell: (r) => formatOutage(r.ev.use1),
                width: 220,
              },
              {
                id: 'usw2',
                header: 'us-west-2 caller-visible outage',
                cell: (r) => formatOutage(r.ev.usw2),
                width: 220,
              },
              {
                id: 'both',
                header: 'Both regions dark at once',
                cell: (r) =>
                  typeof r.ev.bothDarkMs === 'number' && r.ev.bothDarkMs > 0 ? (
                    <StatusIndicator type="error">
                      {r.ev.bothDarkMs.toLocaleString()} ms
                    </StatusIndicator>
                  ) : (
                    <Box variant="small">not observed</Box>
                  ),
                width: 190,
              },
            ]}
          />
          <Alert type="error" header="The PLANNED operation was the worst single-region case">
            <Box variant="p">
              The managed switchover — the rehearsed, RPO-0 operation documented as controlled — cost{' '}
              <strong>{EVAL.events.switchover.use1.outageMs.toLocaleString()} ms</strong> in us-east-1
              and produced a{' '}
              <strong>{EVAL.events.switchover.bothDarkMs.toLocaleString()} ms</strong> window with{' '}
              <strong>both regions dark simultaneously</strong>. Not one acked write was lost in it
              ({EVAL.rpo.switchover.missing} missing of {EVAL.rpo.switchover.expected} acked, RPO 0
              measured) — so this is an AVAILABILITY finding, not a durability one, and the two must
              not be traded off against each other.
            </Box>
          </Alert>
          <Alert type="info" header="The patch asymmetry is evidence, not noise">
            <Box variant="p">
              The same engine patch cost{' '}
              <strong>{EVAL.events.patch.use1.outageMs.toLocaleString()} ms</strong> in us-east-1 and{' '}
              <strong>{EVAL.events.patch.usw2.outageMs.toLocaleString()} ms</strong> in us-west-2. That
              gap is the documented exclusion of a global secondary&apos;s reader from zero-downtime
              patching, showing up as a number — which is what makes this flaw the SURMOUNTABLE kind:
              a second reader gives the reader endpoint somewhere else to route. The two flaws that
              actually decided against UC7 are the ones no configuration change reaches.
            </Box>
          </Alert>
        </SpaceBetween>
      </Container>

      {/*
        THE MEASUREMENTS — ALL EIGHT NOW TAKEN.

        ⚠️ THIS CONTAINER HAS NOW BEEN WRONG IN BOTH DIRECTIONS, WHICH IS WHY THE COUNTS ARE DERIVED.
        It said "0 of 8 run", then "every row unrun" (false once two rows had a measured half), then
        "0 complete" (false once all eight ran). Each correction was the narrowest true statement at
        the time, and each went stale because it was PROSE typed beside a number that derived itself.
        The counter never lied; the sentence next to it did, three times.

        The counter now leads with what IS complete, which is the reverse of the old rule. That rule
        existed so partial progress could not read as progress. It inverts here for the same reason it
        was written: leading with "0 not run" would now understate what was actually driven, and the
        cost of understating it is that a reader discounts the rejection as unevidenced.
      */}
      <Container
        header={
          <Header
            variant="h2"
            counter={`(${TESTS_DONE} complete, ${TESTS_PARTIAL} partial, ${TESTS_NOT_RUN} not run)`}
            description="UC7 was held to the SAME testing standard as UC4, not a lighter one because it was the challenger. Every row here answers what the system does UNDER LOAD or UNDER FAILURE, and four of the answers argue against adopting it — which is what a fair standard looks like when the challenger loses."
          >
            The measurements — all eight driven, and four of them cut against UC7
          </Header>
        }
      >
        <SpaceBetween size="m">
          <StatusIndicator type="success" data-testid="uc7-tests-complete">
            <strong>
              {TESTS_DONE} of {TEST_PLAN.length} complete, {TESTS_PARTIAL} partial, {TESTS_NOT_RUN} not
              run.
            </strong>{' '}
            Each row below is now a finding rather than work. ⚠️ <strong>Complete means MEASURED, not
            passed</strong> — three of these were one-way drills (a live engine patch, a managed
            switchover, an <code>--allow-data-loss</code> failover) and the numbers they returned are
            the reason UC7 was not chosen.
          </StatusIndicator>
          <Table
            variant="embedded"
            wrapLines
            items={TEST_PLAN.map((t, i) => ({ ...t, key: `${t.test}-${i}` }))}
            trackBy="key"
            data-testid="uc7-test-plan"
            columnDefinitions={[
              {
                id: 'test',
                header: 'Measurement',
                cell: (t) => (
                  <SpaceBetween size="xxs">
                    <Box fontWeight="bold">{t.test}</Box>
                    <Badge color={t.status === 'partial' ? 'blue' : 'red'}>{t.status}</Badge>
                  </SpaceBetween>
                ),
                width: 260,
              },
              { id: 'why', header: 'What it would establish', cell: (t) => t.why, minWidth: 300 },
              { id: 'control', header: 'What it must control for', cell: (t) => t.control, minWidth: 320 },
            ]}
          />
        </SpaceBetween>
      </Container>

      <ExpandableSection
        headerText="What UC7 INHERITS from UC1, and must treat as work rather than accepted risk"
        variant="container"
        defaultExpanded
      >
        <SpaceBetween size="m">
          <Box variant="p">
            Keeping UC1&apos;s issuance is the point of UC7 — and it means UC7 also inherits the two
            duplicate paths a full redesign would have deleted structurally. A gap is acceptable; a
            duplicate is catastrophic, and <strong>any duplicate path disqualifies the option</strong>.
            So these are listed as work with an owner-shaped description, not as risks accepted.
          </Box>
          <Table
            variant="embedded"
            wrapLines
            items={INHERITED.map((r, i) => ({ ...r, key: `${r.path}-${i}` }))}
            trackBy="key"
            data-testid="uc7-inherited-table"
            columnDefinitions={[
              { id: 'path', header: 'Duplicate path', cell: (r) => <Box fontWeight="bold">{r.path}</Box>, width: 260 },
              {
                id: 'status',
                header: 'Status',
                cell: (r) => <Badge color={r.tone}>{r.status}</Badge>,
                width: 220,
              },
              { id: 'detail', header: 'Why, and what closes it', cell: (r) => r.detail, minWidth: 380 },
            ]}
          />
        </SpaceBetween>
      </ExpandableSection>

      {/*
        WHY THIS TAB STILL HAS NO PERFORMANCE NUMBERS, SAID OUT LOUD RATHER THAN LEFT AS AN ABSENCE.

        An absence is ambiguous — a reader could take it for an oversight and go looking for the
        figures elsewhere, or worse, carry UC2's across on the grounds that the engine is nominally
        the same. It is not the same: different topology, a different transport, cross-region reads,
        and a capacity ceiling below the recommended minimum. So the absence is stated as a decision.

        ⚠️ THE RULE GOT HARDER TO HOLD WHEN UC7 WAS BUILT, NOT EASIER. While it was a design there
        was no source for a figure. Now there is a build log full of them, measurement is running
        elsewhere as this is written, and every one of those numbers is a tempting paste. The rule did
        not relax: a figure reaches this site through a live API call or through the build-time parser
        over a committed report, and by no other route.
      */}
      <Alert
        type="warning"
        header="Why there is STILL not a single performance figure on this tab, now that there is something to measure"
        data-testid="uc7-no-figures"
      >
        <SpaceBetween size="xs">
          <Box variant="p">
            Every number elsewhere on this site comes from a live API call or from a build-time
            parser over a committed report, and the parser fails the build if the report&apos;s shape
            moves. UC7 has <strong>no published performance report to parse</strong>, so it gets{' '}
            <strong>no figures at all</strong> — not a projection, not an arithmetic estimate, and{' '}
            <strong>not a UC2 latency borrowed because the engine is nominally the same engine</strong>.
            It is not the same: different topology, a different transport, cross-region reads, and a
            capacity ceiling currently below the recommended minimum for a Serverless v2 global
            primary.
          </Box>
          <Box variant="p">
            <strong>
              Being deployed makes this rule harder to hold rather than unnecessary.
            </strong>{' '}
            While UC7 was a design there was no figure to be tempted by. Now the topology has been
            driven, measurement is in progress elsewhere, and every number produced along the way is
            a paste away from this page. It does not get pasted. When performance figures exist they
            belong on the <strong>Load test</strong> tab, derived by the parser that fails the build
            if the report moves — not retyped into a component where nothing can check them.
          </Box>
          <Box variant="p">
            This rule exists because a hand-typed figure from this site once became a wrong number in
            the customer&apos;s own written analysis. A projection printed beside real measurements is
            read as a third measurement.
          </Box>
          <Box variant="p">
            <strong>The one number on this tab</strong> is the round-trip duration of the vend you
            just triggered, measured in your own browser at the moment you pressed the button — the
            same treatment every other panel gives its own round trip, and a single client-side
            sample rather than a benchmark.
          </Box>
        </SpaceBetween>
      </Alert>

      {/*
        NO COMPLIANCE OR AUTHORISATION SECTION HERE, for the same reason UC4 has none. A-U2 assumes
        no boundary applies for SCOPING ONLY and licenses no claim in either direction. Naming one
        would present an unestablished constraint as established, and could eliminate a real option
        for a reason nobody knows to be true.
      */}

      <Box variant="small">
        Sources on disk: <code>docs/greenfield/uc7-build-log.md</code> (what was actually built and
        every finding above, each labelled MEASURED, READ-FROM-DOCS or ARITHMETIC — including the two
        that cut against UC7 and the defects found while building it),{' '}
        <code>services/rules-api/aurora-pg-executor.js</code> (the PostgreSQL-wire transport the Data
        API failure forced, with its reasoning in place),{' '}
        <code>services/rules-api/aurora-global.test.js</code> (the equivalence proof: SQL identity,
        the byte-for-byte wire text, and both stores driven against one shared database — plus the
        assertion that &quot;active-active&quot; appears nowhere in a <code>/v7</code> response),{' '}
        <code>infra/09-rules-global.yaml</code> (the topology as infrastructure code, imported rather
        than recreated so the template and reality agree),{' '}
        <code>docs/UC7-AURORA-PG-RULES-PLAN.md</code> (the plan, with the drill schedule and the cost
        lines), <code>docs/greenfield/RESEARCH-PACK.md</code> (the verified AWS facts), and{' '}
        <code>docs/greenfield/CONTEXT.md</code> (A-U1 and A-U2, which are OUR assumptions and not the
        customer&apos;s words).
      </Box>
    </SpaceBetween>
  );
}
